import {type FulfillmentDetailAddressInput} from '@/graphql/types';
import {
  geocodedAddressResultToFulfillmentDetailAddressInput,
  geocodedAddressResultToString,
} from '@/utils/geocodedAddressResult';
import {geocodeStringToFulfillmentAddress} from '@/utils/geocodeStringToFulfillmentAddress';
import {type GeocodedAddress, type GeocodedAddressResult} from '@/utils/googleAutocomplete';

// The subset of the keyword form's values that address resolution reads.
type ResolveKeywordFormAddressValues = {
  address?: FulfillmentDetailAddressInput;
  lastResolvedAddressString: string;
  addressString: string;
  geocodedAddressResult: GeocodedAddressResult | null;
  googlePlace: {description: string} | null;
};

type ResolveKeywordFormAddressOptions = {
  fallbackAddress?: FulfillmentDetailAddressInput;
  fallbackAddressString: string;
  fetchGeocodedAddress: (addressString: string) => Promise<GeocodedAddress[]>;
};

/**
 * Resolves the address to search with (on submit / suggestion click) for the
 * homepage keyword form. Extracted from StartOrderFormKeywordLocation so the
 * resolution logic — in particular the reuse branch below — is unit testable
 * without rendering the whole form.
 */
export const resolveKeywordFormAddress = async (
  values: ResolveKeywordFormAddressValues,
  {fallbackAddress, fallbackAddressString, fetchGeocodedAddress}: ResolveKeywordFormAddressOptions,
): Promise<FulfillmentDetailAddressInput | undefined> => {
  if (values.googlePlace) {
    return geocodeStringToFulfillmentAddress(values.googlePlace.description, {
      fetchGeocodedAddress,
      fallbackAddress,
    });
  }
  if (
    values.geocodedAddressResult &&
    geocodedAddressResultToString(values.geocodedAddressResult) === values.addressString
  ) {
    return geocodedAddressResultToFulfillmentDetailAddressInput(values.geocodedAddressResult);
  }
  // The blur handler (useManualAddressResolver) already geocodes the entered
  // address into `address` and records the input it resolved from. When that
  // still matches the current input, reuse it to skip a redundant geocode on
  // the search-start hot path. Only helps the "type → tab away → submit" flow;
  // a direct "type → click Search" doesn't pre-resolve in time, so the marker
  // mismatches and it falls through to the geocode below. Placed after the
  // geolocation branch so geolocation results (handled above) always win.
  if (values.address && values.lastResolvedAddressString === values.addressString.trim()) {
    return values.address;
  }
  if (values.addressString && values.addressString !== fallbackAddressString) {
    return geocodeStringToFulfillmentAddress(values.addressString, {
      fetchGeocodedAddress,
      fallbackAddress,
    });
  }
  return fallbackAddress ?? undefined;
};
